import _ from 'lodash';
import React from 'react';
import styled from 'styled-components';

import {
  FormHeader, Desc, SmallDesc, InputContainer, SPassword, Hint, Button, HelpBlock,
} from '../common/styles';
import Loading from '../../../shared/loading';
import Password from '../../shared/sign-up/password';
import { useForm } from '../../../shared/form-validator-hook';
import { usePutPassword } from '../../../services/auth-service';
import ResetPasswordConext from './context';

/* stylelint-disable selector-type-no-unknown */
const Container = styled.div`
  background-color: ${props => props.theme.bodyBackgroundColor};
  border-radius: ${props => (props.theme.isMobile ? '4px' : '0')};
  display: flex;
  flex-direction: column;
  align-items: center;
  width: ${props => (props.theme.isMobile ? '92%' : '33vw')};
  margin-top: ${props => (props.theme.isMobile ? '20px' : '0px')};
  ${props => (props.theme.isMobile ? `
    padding: 35px 15px 15px 15px;
    border-radius: 4px;
    border: 1px solid ${props.theme.secondaryColor30Transparent};
    justify-content: flex-start;
  ` : `
    max-width: 480px;
    justify-content: center;
    padding: 40px;
  `)}

  > ${Desc} {
    margin-top: 15px;
  }

  > ${SmallDesc} {
    margin-top: ${props => (props.theme.isMobile ? '73px' : '117px')};
    margin-bottom: 50px;
  }

  > ${HelpBlock} {
    width: 100%;
    max-width: 320px;
    margin-top: 1px;
    margin-bottom: -14px;
  }

  ${SPassword}[data-error="show"] + span.icon-lw + ${HelpBlock} + ${Hint} {
    display: none;
  }

  ${Button} {
    width: 100%;
  }
`;
/* stylelint-enable selector-type-no-unknown */

function getError(error) {
  if (_.get(error, 'response.status') === 422) {
    const allErrors = _.get(error, 'response.data.errors', {});
    const passwordErr = _.get(allErrors, 'password[0]');
    if (passwordErr) {
      return `Password ${passwordErr}`;
    }
    const tokenError = _.get(allErrors, 'reset_password_token[0]');
    if (tokenError) {
      return 'Reset password link is invalid';
    }
  }
  return null;
}

export default function ResetForm() {
  const { dispatch } = React.useContext(ResetPasswordConext);
  const loadingRef = React.useRef();

  const {
    values, useInput, errors, validateForm,
  } = useForm({ password: '' }, { lazy: true });

  const [{ response, error, loading }, resetPassword] = usePutPassword();

  const [resetPasswordError, setResetPasswordError] = React.useState(null);

  React.useEffect(() => {
    if (loading) {
      loadingRef.current.start();
    }
  }, [loading]);

  React.useEffect(() => {
    const msg = getError(error);
    if (msg) {
      loadingRef.current.stop();
      setResetPasswordError(msg);
    }
  }, [error]);

  React.useEffect(() => {
    setResetPasswordError(null);
  }, [values]);

  React.useEffect(() => {
    if (_.get(response, 'status') === 204) {
      setResetPasswordError(null);
      dispatch({ type: 'setComplete' });
    }
  }, [response, dispatch]);

  function submit(e) {
    e && e.preventDefault();

    if (validateForm()) {
      const token = (new URL(window.location)).searchParams.get('reset_password_token');
      resetPassword(values.password, token);
    }
  }

  return (
    <Container>
      <FormHeader>
        Let’s reset your password
      </FormHeader>
      <Desc>
        Please enter your new password below. We’ll send you an email to confirm your update.
      </Desc>
      <InputContainer>
        <Password Field={SPassword} Err={HelpBlock} useInput={useInput} errors={errors} peekaboo />
        {!resetPasswordError && <Hint>Password requires at least 8 characters and 1 digit</Hint>}
      </InputContainer>
      {!!resetPasswordError && <HelpBlock data-error="show">{resetPasswordError}</HelpBlock>}
      <InputContainer>
        <Loading variant="button" ref={loadingRef}>
          <Button type="button" onClick={submit}>reset my password</Button>
        </Loading>
      </InputContainer>
      <SmallDesc>
        <a href="/sign_in">
          Return to Login
        </a>
      </SmallDesc>
    </Container>
  );
}
